export interface Redactor {
    /**
     * Traverses the object and replaces all values of the keys that are in
     * provided `keysToRedact` with respective replacement and return a cloned
     * object.
     *
     * @param obj - The object to redact.
     */
    redactObj(obj: Record<string, unknown>): Record<string, unknown>;
    /**
     * Redact the provided string by replacing all occurrences of the patterns
     * and return a new string with redacted values.
     *
     * @param text - The string to redact.
     *
     * @example
     *
     * ```typescript
     * const redactor = createRedactor(...);
     * redactor.addPattern('AABBCC', '*****');
     * redactor.addPattern('12345', '[Secret Number]');
     *
     * redactor.redactStr('some secret AABBCC and 12345'); // Output 'some secret ***** and [Secret Number]'
     * ```
     */
    redactStr(text: string): string;
    /**
     * Add a pattern to the redactor and an optional mask for the redaction.
     *
     * @param pattern - The pattern to add.
     * @param replaceTo - The mask to use for redaction.
     *
     * @example
     *
     * ```typescript
     * const redactor = createRedactor(...);
     * redactor.addPattern('AABBCC', '*****');
     * redactor.addPattern('12345', '[Secret Number]');
     *
     * redactor.redactStr('some secret AABBCC and 12345'); // Output 'some secret ***** and [Secret Number]'
     * ```
     */
    addPattern(pattern: string, replaceTo?: string): void;
    /**
     * Remove a pattern from the redactor.
     *
     * @param pattern - The pattern to remove.
     */
    removePattern(pattern: string): boolean;
    /**
     * Clear all patterns from the redactor.
     */
    clearPatterns(): void;
}

export interface RedactorOptions {
    defaultReplaceTo: string;
    keysToRedact: [string, string][];
}

type Pattern = [string, string];
type Brand<DataType> = string & {__dataType: DataType};
type SecretKey = Brand<'SecretKey'>;
type SecretPattern = Brand<'SecretPattern'>;
type SecretMask = Brand<'SecretMask'>;

const isBrandedString = <DataType>(s: unknown): s is Brand<DataType> => {
    if (typeof s === 'string' && s) {
        return true;
    }
    return false;
};

function assertBrandedString<DataType>(
    s: unknown,
): asserts s is Brand<DataType> {
    if (!isBrandedString<DataType>(s)) {
        throw new Error(`Expected a proper branded string: ${s}`, {cause: s});
    }
}

function toBrandedString<DataType>(s: unknown) {
    assertBrandedString<DataType>(s);
    return s;
}

export const createRedactor = (options: RedactorOptions) => {
    const secretKeys = new Map<SecretKey, SecretMask>(
        options.keysToRedact.map(o => [
            toBrandedString<'SecretKey'>(o[0]),
            toBrandedString<'SecretMask'>(o[1]),
        ]),
    );
    const secretPatternMap = new Map<SecretPattern, SecretMask>();

    const redact = (str: string, patterns: Pattern[]) => {
        if (!str || !patterns.length) {
            return str;
        }
        let strSlice = str;
        let result: string[] = [];
        const [patternReplace, ...rest] = patterns;
        const [pattern, replace] = patternReplace ?? [];
        while (strSlice && pattern && replace) {
            const idx = strSlice.indexOf(pattern);
            if (idx === -1) {
                break;
            }
            result.push(redact(strSlice.slice(0, idx), rest));
            result.push(replace);
            strSlice = strSlice.slice(idx + pattern.length);
        }
        result.push(redact(strSlice, rest));
        return result.join('');
    };

    // DFS object traversal with cycle detection
    const dfs = (
        obj: Record<string, unknown>,
        visited: Map<unknown, unknown>,
    ) => {
        const old = visited.get(obj);
        if (old) {
            // Cycle detected
            return old as Record<string, unknown>;
        }
        const clone = {...obj};
        visited.set(obj, clone);
        for (const [key, value] of Object.entries(clone)) {
            const k = toBrandedString<'SecretKey'>(key);
            if (secretKeys.has(k)) {
                clone[key] = secretKeys.get(k);
                continue;
            }
            if (typeof value === 'object') {
                if (value !== null) {
                    clone[key] = dfs(value as Record<string, unknown>, visited);
                }
            }
        }
        return clone;
    };

    return Object.freeze<Redactor>({
        redactObj(obj) {
            return dfs(obj, new Map());
        },
        redactStr(text) {
            return redact(text, Array.from(secretPatternMap));
        },
        addPattern(pattern, replaceTo = options.defaultReplaceTo) {
            const trimmed = toBrandedString<'SecretPattern'>(pattern);
            if (trimmed && !secretPatternMap.has(trimmed)) {
                secretPatternMap.set(
                    trimmed,
                    toBrandedString<'SecretMask'>(replaceTo),
                );
            }
        },
        removePattern(pattern) {
            return secretPatternMap.delete(
                toBrandedString<'SecretPattern'>(pattern),
            );
        },
        clearPatterns() {
            secretPatternMap.clear();
        },
    });
};
